/**
 * PLP App — Root Preact component.
 * Queries Algolia searchClient directly for collections and search pages.
 * Infinite scroll at 75% viewport depth.
 */
(function () {
  const { html, render, useState, useEffect, useCallback, useRef } = window.preact;

  // ── Algolia helpers ──────────────────────────────────────────────────────

  function getClient() {
    return window.algoliaShopify && window.algoliaShopify.searchClient;
  }

  function getIndexName() {
    const cfg = (window.algoliaShopify && window.algoliaShopify.config) || {};
    return (cfg.index_prefix || 'shopify_') + 'products';
  }

  function getContext() {
    const el = document.getElementById('plp-collection-data');
    let data = {};
    if (el) { try { data = JSON.parse(el.textContent); } catch (e) {} }
    return {
      pageType: data.pageType || 'collection',
      collectionTitle: data.collectionTitle || (window.algoliaCollectionContext && window.algoliaCollectionContext.title) || '',
      collectionHandle: data.collectionHandle || '',
      searchQuery: data.searchQuery || '',
    };
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Decode HTML entities (e.g. &ndash;) from Algolia product titles
  var _decodeEl;
  function decodeEntities(str) {
    if (!str) return str;
    if (!_decodeEl) _decodeEl = document.createElement('textarea');
    _decodeEl.innerHTML = str;
    return _decodeEl.value;
  }

  // Pick a PLP card's main image. Algolia's `distinct` returns one representative
  // VARIANT record per product, and the card deep-links to THAT variant
  // (?variant=objectID). So the card image must be the representative variant's
  // own image (hit.image) — otherwise the photo shown on the card won't match the
  // image the PDP shows after the click. Fall back to the product image only when
  // the variant has none. NOTE: kept in sync with the helper in assets/algolia-native.js.
  function pickPlpCardImage(hit) {
    if (!hit) return '';
    return hit.image || hit.product_image || '';
  }

  // Map an Algolia hit to the shape our card components expect
  function mapHit(hit, page, perPage, idx, queryID) {
    return {
      objectID: hit.objectID,
      productId: hit.id, // Shopify product id — used for Yotpo ratings lookup
      title: decodeEntities(hit.title || ''),
      handle: hit.handle || '',
      // Deep-link to the representative variant (the one the card shows) so the
      // PDP opens on the searched/selected variant, not the default one.
      url: '/products/' + (hit.handle || '') + (((hit.variant_id || hit.objectID) && (hit.variants_count || 0) > 1) ? ('?variant=' + (hit.variant_id || hit.objectID)) : ''),
      image: pickPlpCardImage(hit),
      price: Math.round((hit.price || 0) * 100),
      priceMin: hit.variants_min_price != null ? Math.round(hit.variants_min_price * 100) : null,
      priceMax: hit.variants_max_price != null ? Math.round(hit.variants_max_price * 100) : null,
      compareAtPrice: hit.compare_at_price ? Math.round(hit.compare_at_price * 100) : null,
      vendor: hit.vendor || '',
      productType: hit.product_type || '',
      available: hit.inventory_available !== false,
      tags: hit.tags || [],
      createdAt: hit.created_at_i || hit.published_at_i || 0,
      rating: 0,
      ratingCount: 0,
      description: (function () {
        var short = hit.meta && hit.meta.custom && hit.meta.custom.short_description;
        if (short) return short.replace(/<[^>]*>/g, '');
        var full = (hit.body_html_safe || '').replace(/<[^>]*>/g, '');
        return full.length > 200 ? full.substring(0, 200) + '...' : full;
      })(),
      variantId: hit.variant_id || hit.objectID,
      sku: hit.sku || '',
      variantsCount: hit.variants_count || 0,
      _queryID: queryID,
      _position: page * perPage + idx + 1,
    };
  }

  // Facets we never surface (variant-option noise + duplicates of price)
  var FACET_SKIP = /^options(\.|$)|^option_names$|^price_range$|^named_tags/;
  var FACET_LABELS = {
    vendor: 'Brand',
    product_type: 'Product Type',
    tags: 'Tags',
    inventory_available: 'Availability',
    collections: 'Collection',
    price: 'Price',
  };
  function facetLabel(attr) {
    return FACET_LABELS[attr] || attr.replace(/[_.]/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Build an ordered list of facet groups straight from the Algolia response.
  // No hardcoded facet list: whatever the index (and the Algolia dashboard's
  // facetOrdering, when present) exposes is what shows up. If nothing comes
  // back, no facet groups render.
  function buildFacets(raw, stats, order, ctx) {
    raw = raw || {};
    var ps = stats && stats.price;
    var attrs = (order && order.length ? order : Object.keys(raw)).filter(function (a) { return !FACET_SKIP.test(a); });
    var groups = [];
    var sawPrice = false;
    attrs.forEach(function (attr) {
      if (attr === 'price') {
        sawPrice = true;
        groups.push({ attribute: 'price', label: 'Price', type: 'price', min: ps ? Math.round(ps.min * 100) : 0, max: ps ? Math.round(ps.max * 100) : 100000 });
        return;
      }
      var values = raw[attr];
      if (!values || !Object.keys(values).length) return;
      if (attr === 'inventory_available') {
        var av = {};
        if (values['true']) av['In Stock'] = values['true'];
        if (values['false']) av['Out of Stock'] = values['false'];
        if (Object.keys(av).length) groups.push({ attribute: attr, label: 'Availability', type: 'checkbox', values: av });
        return;
      }
      // Drop the collection's own tag — every product in a collection page already
      // carries it, so it'd be a redundant no-op refinement.
      if (attr === 'tags' && ctx && ctx.collectionTitle && values[ctx.collectionTitle] != null) {
        var pruned = {};
        Object.keys(values).forEach(function (k) { if (k !== ctx.collectionTitle) pruned[k] = values[k]; });
        values = pruned;
        if (!Object.keys(values).length) return;
      }
      groups.push({ attribute: attr, label: facetLabel(attr), type: 'checkbox', values: values });
    });
    if (!sawPrice && ps) {
      groups.push({ attribute: 'price', label: 'Price', type: 'price', min: Math.round(ps.min * 100), max: Math.round(ps.max * 100) });
    }
    return groups;
  }

  // Build Algolia filter string generically from whatever facet attributes
  // are active in the filters object (keyed by raw Algolia attribute name).
  function buildFilters(ctx, filters) {
    const parts = [];
    if (ctx.pageType === 'collection' && ctx.collectionTitle && ctx.collectionHandle !== 'all') {
      parts.push('tags:"' + ctx.collectionTitle + '"');
    }
    Object.keys(filters).forEach(function (key) {
      if (key === 'priceRange') return;
      var vals = filters[key];
      if (!Array.isArray(vals) || !vals.length) return;
      var clauses = vals.map(function (v) {
        if (key === 'inventory_available') return 'inventory_available:' + (v === 'In Stock' ? 'true' : 'false');
        return key + ':"' + String(v).replace(/"/g, '\\"') + '"';
      });
      parts.push('(' + clauses.join(' OR ') + ')');
    });
    return parts.join(' AND ');
  }

  function buildNumericFilters(filters) {
    const nf = [];
    if (filters.priceRange) {
      if (filters.priceRange.min != null) nf.push('price>=' + (filters.priceRange.min / 100));
      if (filters.priceRange.max != null) nf.push('price<=' + (filters.priceRange.max / 100));
    }
    return nf;
  }

  // ── Search function ──────────────────────────────────────────────────────

  function searchAlgolia(ctx, params) {
    const client = getClient();
    if (!client) return Promise.resolve(null);

    const sp = {
      query: ctx.pageType === 'search' ? ctx.searchQuery : '',
      hitsPerPage: params.hitsPerPage || 24,
      page: params.page || 0,
      // Pull every facet the index exposes (+ inventory_available, which is
      // filterOnly and only returns counts when requested explicitly). The
      // Algolia dashboard decides what's relevant; we render whatever returns.
      facets: ['*', 'inventory_available'],
      clickAnalytics: true,
      distinct: true,
    };
    if (window.__algoliaUserToken) sp.userToken = window.__algoliaUserToken;

    const filterStr = buildFilters(ctx, params.filters || {});
    if (filterStr) sp.filters = filterStr;

    const nf = buildNumericFilters(params.filters || {});
    if (nf.length) sp.numericFilters = nf;

    return client.search([{ indexName: getIndexName(), params: sp }]).then((res) => {
      const r = res.results[0];
      const hits = (r.hits || []).map((h, i) => mapHit(h, r.page, r.hitsPerPage, i, r.queryID));
      const order = r.renderingContent && r.renderingContent.facetOrdering && r.renderingContent.facetOrdering.facets && r.renderingContent.facetOrdering.facets.order;
      return {
        hits: hits,
        nbHits: r.nbHits || 0,
        nbPages: r.nbPages || 0,
        page: r.page || 0,
        facets: buildFacets(r.facets || {}, r.facets_stats || {}, order, ctx),
      };
    });
  }

  // ── URL ↔ filter sync ────────────────────────────────────────────────────

  // Params that are NOT facet refinements (own state, price, or tracking noise)
  var URL_NON_FACET = /^(price_min|price_max|page|q|sort|view|variant|ref|fbclid|gclid|msclkid|_kx)$|^utm_/i;

  function filtersFromURL() {
    var sp = new URLSearchParams(window.location.search);
    var f = {
      priceRange: {
        min: sp.get('price_min') ? Number(sp.get('price_min')) : null,
        max: sp.get('price_max') ? Number(sp.get('price_max')) : null,
      },
    };
    var seen = {};
    sp.forEach(function (_v, key) {
      if (URL_NON_FACET.test(key) || seen[key]) return;
      seen[key] = 1;
      f[key] = sp.getAll(key);
    });
    return f;
  }

  function filtersToURL(filters) {
    var prev = new URLSearchParams(window.location.search);
    var sp = new URLSearchParams();
    // Preserve non-facet params we don't own (page/q/sort/view)
    prev.forEach(function (v, k) {
      if (k === 'page' || k === 'q' || k === 'sort' || k === 'view') sp.set(k, v);
    });
    Object.keys(filters).forEach(function (key) {
      if (key === 'priceRange') {
        if (filters.priceRange && filters.priceRange.min != null) sp.set('price_min', filters.priceRange.min);
        if (filters.priceRange && filters.priceRange.max != null) sp.set('price_max', filters.priceRange.max);
      } else if (Array.isArray(filters[key]) && filters[key].length) {
        filters[key].forEach(function (v) { sp.append(key, v); });
      }
    });
    var qs = sp.toString();
    history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  // ── App component ────────────────────────────────────────────────────────

  function PlpApp() {
    const ctx = getContext();

    const [viewMode, setViewMode] = useState(localStorage.getItem('plp-view-mode') || 'grid');
    const [sortBy, setSortBy] = useState('relevance');
    const [filters, setFilters] = useState(filtersFromURL);
    const [products, setProducts] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [facets, setFacets] = useState(null);
    const [loading, setLoading] = useState(true);
    const [, setHasMore] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [ratings, setRatings] = useState({}); // productId -> { avg, count } from Yotpo

    const pageRef = useRef(0);
    const hasMoreRef = useRef(false);
    const loadingRef = useRef(false);
    const ratingReqRef = useRef(new Set()); // product ids already requested from Yotpo
    const hitsPerPage = 24;

    function fetch_(page, append) {
      if (!append) setLoading(true);
      loadingRef.current = true;

      searchAlgolia(ctx, { page, hitsPerPage, filters })
        .then((res) => {
          if (!res) { setLoading(false); loadingRef.current = false; return; }
          if (append) {
            setProducts((prev) => prev.concat(res.hits));
          } else {
            setProducts(res.hits);
          }
          setTotalResults(res.nbHits);
          setFacets(res.facets);
          const more = page < res.nbPages - 1;
          setHasMore(more);
          hasMoreRef.current = more;
          pageRef.current = page;
          setLoading(false);
          loadingRef.current = false;
          document.getElementById('plp-app')?.classList.add('plp-ready');
          var b = document.querySelector('.acb-banner');
          if (b) { b.style.visibility = 'visible'; b.style.opacity = '1'; }
        })
        .catch(() => {
          setLoading(false);
          loadingRef.current = false;
          document.getElementById('plp-app')?.classList.add('plp-ready');
          var b = document.querySelector('.acb-banner');
          if (b) { b.style.visibility = 'visible'; b.style.opacity = '1'; }
        });
    }

    const doFetch = useCallback(() => {
      pageRef.current = 0;
      hasMoreRef.current = false;
      fetch_(0, false);
    }, [filters]);

    useEffect(doFetch, [doFetch]);

    // Sync filters → URL
    useEffect(() => { filtersToURL(filters); }, [filters]);

    // Fetch Yotpo ratings for visible products (collection PLP). Cards with 0
    // reviews are hidden by the StarRating component, so only real ratings show.
    useEffect(() => {
      if (!products || !products.length) return;
      var YOTPO_KEY = 'bzwqO2raPh4O7OoNJaS3vueIBxaZuBnxORwqBmmi';
      products.forEach(function (p) {
        var pid = p.productId != null ? String(p.productId) : '';
        if (!pid || ratingReqRef.current.has(pid)) return;
        ratingReqRef.current.add(pid);
        window.fetch('https://api-cdn.yotpo.com/v1/widget/' + YOTPO_KEY + '/products/' + pid + '/reviews.json?per_page=1')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var bl = data && data.response && data.response.bottomline;
            if (!bl) return;
            var count = bl.total_review || 0;
            if (!count) return; // no reviews — leave hidden
            setRatings(function (prev) {
              var next = Object.assign({}, prev);
              next[pid] = { avg: bl.average_score || 0, count: count };
              return next;
            });
          })
          .catch(function () {});
      });
    }, [products]);

    // Move banner into content column after Preact renders .plp-layout__main
    useEffect(() => {
      const banner = document.querySelector('.acb-banner');
      const main = document.querySelector('.plp-layout__main');
      if (banner && main && !banner.closest('.plp-layout__main')) {
        main.insertBefore(banner, main.firstChild);
      }
    }, []);

    // Infinite scroll at 75%
    useEffect(() => {
      function onScroll() {
        if (loadingRef.current || !hasMoreRef.current) return;
        if ((window.scrollY + window.innerHeight) >= document.documentElement.scrollHeight * 0.75) {
          fetch_(pageRef.current + 1, true);
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, [filters]);

    // ── Handlers ─────────────────────────────────────────────────────────

    function sortProducts(prods, sort) {
      if (sort === 'price-asc')  return prods.slice().sort(function(a, b) { return (a.price || 0) - (b.price || 0); });
      if (sort === 'price-desc') return prods.slice().sort(function(a, b) { return (b.price || 0) - (a.price || 0); });
      if (sort === 'newest')     return prods.slice().sort(function(a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
      return prods; // 'relevance' — keep Algolia order
    }

    function toggleView(mode) { setViewMode(mode); localStorage.setItem('plp-view-mode', mode); }
    function handleSort(val) { setSortBy(val); }

    function toggleFilter(key, value) {
      setFilters((prev) => {
        const cur = prev[key] || [];
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : cur.concat([value]);
        return { ...prev, [key]: next };
      });
    }

    function handlePrice(bound, value) {
      setFilters((prev) => ({
        ...prev,
        priceRange: { ...prev.priceRange, [bound]: value },
      }));
    }

    function handleAddToCart(variantId, quantity) {
      function openDrawer() {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) {
          drawer.open();
        }
      }

      if (window.CartHandler && typeof window.CartHandler.add === 'function') {
        window.CartHandler.add(variantId, quantity)
          .then(openDrawer)
          .catch(function (err) { console.error('PLP add to cart failed', err); });
        return;
      }

      // Fallback: request the cart-drawer section so we can render + open it.
      var drawer = document.querySelector('cart-drawer');
      var body = { id: variantId, quantity: quantity };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }
      window.fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw data;
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data); // renders + opens the drawer
          } else {
            openDrawer();
          }
        })
        .catch(function (err) { console.error('PLP add to cart failed', err); });
    }

    // ── Drawer portal — render directly on document.body so z-index is
    //    evaluated in the root stacking context, above the sticky header.
    const drawerPortalRef = useRef(null);
    if (!drawerPortalRef.current) {
      const el = document.createElement('div');
      el.id = 'plp-filter-drawer-portal';
      document.body.appendChild(el);
      drawerPortalRef.current = el;
    }

    useEffect(() => {
      render(html`<${window.PlpFilterDrawer}
        isOpen=${drawerOpen}
        onClose=${() => setDrawerOpen(false)}
        facets=${facets}
        filters=${filters}
        onToggleFilter=${toggleFilter}
        onPriceChange=${handlePrice}
        totalResults=${totalResults}
      />`, drawerPortalRef.current);
    }, [drawerOpen, facets, filters, totalResults]);

    // ── Render ────────────────────────────────────────────────────────────

    return html`
      <div class="plp-layout">
        <div class="plp-layout__body">
          <${window.PlpFilterSidebar}
            facets=${facets}
            filters=${filters}
            onToggleFilter=${toggleFilter}
            onPriceChange=${handlePrice}
            collectionTitle=${ctx.collectionTitle}
            collectionUrl=${ctx.collectionHandle ? '/collections/' + ctx.collectionHandle : ''}
            pageType=${ctx.pageType}
            searchQuery=${ctx.searchQuery}
          />
          <div class="plp-layout__main">
            <${window.PlpToolbar}
              totalResults=${totalResults}
              viewMode=${viewMode}
              sortBy=${sortBy}
              hitsPerPage=${hitsPerPage}
              pageType=${ctx.pageType}
              searchQuery=${ctx.searchQuery}
              collectionTitle=${ctx.collectionTitle}
              onToggleView=${toggleView}
              onSortChange=${handleSort}
              onHitsPerPageChange=${() => {}}
              onOpenFilters=${() => setDrawerOpen(true)}
            />
            <${window.PlpProductGrid}
              products=${sortProducts(products, sortBy).map(function (p) {
                var r = ratings[String(p.productId)];
                return r ? Object.assign({}, p, { rating: r.avg, ratingCount: r.count }) : p;
              })}
              viewMode=${viewMode}
              onAddToCart=${handleAddToCart}
            />
          </div>
        </div>
      </div>
    `;
  }

  // Expose formatPrice globally for card components
  window.PlpMockData = { formatPrice, isAlgoliaAvailable: () => !!getClient() };

  // True once the Algolia InstantSearch app block has booted its search instance
  // (or already painted its .ais-page DOM).
  function appBlockActive() {
    return !!(window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search)
      || !!document.querySelector('.ais-page');
  }

  function mount() {
    const dataEl = document.getElementById('plp-collection-data');
    const el = document.getElementById('plp-app');
    if (!dataEl || !el) return;

    let pageType;
    try { pageType = JSON.parse(dataEl.textContent).pageType; } catch (e) { return; }

    // Both search AND collection are now driven by the Algolia InstantSearch app
    // block: algolia-native.js renders our Preact cards from the app block's
    // search instance (the reliable, dashboard-configured path), and the CSS rule
    // `body:has(.ais-page) #plp-app { display:none }` hides this container once it
    // paints. Reveal #plp-app immediately so it isn't stuck behind the preload veil.
    el.classList.add('plp-ready');

    // Search has always deferred to algolia-native.js.
    if (pageType !== 'collection') return;

    // Collection: defer to the app block too. Keep this standalone direct-query
    // app ONLY as a fallback for when the app block never initialises (e.g. the
    // dashboard flag `instant_search_enabled_on_collection` is off) so the page
    // is never left blank.
    if (appBlockActive()) return;

    let fellBack = false;
    function fallback() {
      if (fellBack || appBlockActive()) return;
      fellBack = true;
      render(html`<${PlpApp} />`, el);
    }
    const timer = setTimeout(fallback, 5000);
    // If the app block boots, cancel the fallback and let it own the render.
    document.addEventListener('algolia.hooks.initialize', function () { clearTimeout(timer); }, { once: true });
  }

  // Safety net: always reveal after 10s in case fetch never completes
  setTimeout(() => {
    document.getElementById('plp-app')?.classList.add('plp-ready');
    var b = document.querySelector('.acb-banner');
    if (b) { b.style.visibility = 'visible'; b.style.opacity = '1'; }
  }, 10000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
